import React, { useEffect, useState } from "react";
import { Checkbox, Form, Input, Select, Tag } from "antd";
import FilterContainer, { CoreFiltersBaseProps } from "../../../../components/FilterContainer";
import DateRangePicker from "../../../../../../components/forms/controls/DateRangePicker";
import { useGetAllUsersQuery } from "../../../../../../store/services/api/user-api";
import { useGetAllBuildingsQuery, useGetAllFloorsQuery, useGetAllLocationsQuery } from "../../../../../../store/services/api/building-api";
import { useGetAllAssetTypesQuery, useGetAllSubAssetTypesQuery } from "../../../../../../store/services/api/asset-type-api";
import { DateTime } from "luxon";
import {
  useGetAllSitesQuery,
  useGetAllTeamsQuery,
} from "../../../../../../store/services/api/site-api";

import { Collapse } from "antd";
const { Panel } = Collapse;

type Building = {
  id: string;
  name: string;
};

type Floor = {
  id: string;
  name: string;
  buildingId: string;
};

type Location = {
  id: string;
  name: string;
  floorId: string;
};

export default function TicketsListScreenFilters({ filter, onSubmit }: CoreFiltersBaseProps) {
  const { data: allUsers } = useGetAllUsersQuery();
  const { data: buildings } = useGetAllBuildingsQuery(null);
  const { data: floors } = useGetAllFloorsQuery(null);
  const { data: locations } = useGetAllLocationsQuery(null);
  const { data: assetTypes } = useGetAllAssetTypesQuery(null);
  const { data: subAssetTypes } = useGetAllSubAssetTypesQuery(null);

  const { data: sites } = useGetAllSitesQuery(null);
  const { data: allTeams } = useGetAllTeamsQuery(null);
  const [filteredTeams, setFilteredTeams] = useState<any[]>([]);

  const handleOnSiteChange = (siteIds: string[] | null) => {
    const newSiteIds = siteIds || [];
    filter.setFilter('siteIds', newSiteIds);
    if (!newSiteIds || !allTeams) {
      setFilteredTeams([]);
      filter.setFilter('teamIds', []);
      return;
    }
    const validTeams = allTeams.filter(t => newSiteIds.includes(t.siteId));
    const teamsWithLabels = validTeams.map(t => {
      const site = sites?.find(s => s.id === t.siteId);
      return { ...t, label: `[${site?.name ?? ''}] ${t.name}` };
    });
    teamsWithLabels.sort((a, b) => {
      const siteA = a.label.match(/\[(.*?)\]/)?.[1] ?? '';
      const siteB = b.label.match(/\[(.*?)\]/)?.[1] ?? '';
      return siteA === siteB ? a.name.localeCompare(b.name) : siteA.localeCompare(siteB);
    });
    setFilteredTeams(teamsWithLabels);
    const currentSelectedTeams: string[] = filter.getFilter('teamIds') || [];
    const validTeamIds = teamsWithLabels.map(t => t.id);
    const prunedSelectedTeams = currentSelectedTeams.filter(tId =>
      validTeamIds.includes(tId)
    );
    filter.setFilter('teamIds', prunedSelectedTeams);
  };

  const teamLabelMap: Map<string, string> = new Map(
    filteredTeams.map(t => [t.id, t.label])
  );

  const teamTagRender = (props: any) => {
    const { value, closable, onClose } = props;
    const label = teamLabelMap.get(value) ?? '';
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{
          margin: '2px',
          padding: '2px 6px',
          maxWidth: '100%',
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          height: 'auto',
        }}
      >
        {label}
      </Tag>
    );
  };

     

  const buildingMap: Map<string, string> = new Map(
    (buildings || []).map((b: Building) => [b.id, b.name])
  );
  const floorMap: Map<string, { name: string; buildingId: string }> = new Map(
    (floors || []).map((f: Floor) => [f.id, { name: f.name, buildingId: f.buildingId }])
  );


  const selectedBuildingIds: string[] = filter.getFilter('buildingId') || [];
  const selectedFloorIds: string[] = filter.getFilter('floorId') || [];
  const selectedLocationIds: string[] = filter.getFilter('locationId') || [];
  const selectedSiteIds: string[] = filter.getFilter('siteIds') || [];
  const selectedTeamIds: string[] = filter.getFilter('teamIds') || [];

  const selectedAssetTypeIds: string[] = filter.getFilter("assetTypeId") || [];
  const selectedSubAssetTypeIds: string[] = filter.getFilter("subAssetTypeId") || [];
    

  const handleOnBuildingChange = (buildingIds: string[]) => {
    filter.setFilter('buildingId', buildingIds);


    const currentFloors: string[] = filter.getFilter('floorId') || [];
    const validFloors = (floors || [])
      .filter(f => buildingIds.includes(f.buildingId))
      .map(f => f.id);

    const prunedFloors = currentFloors.filter(floorId =>
      validFloors.includes(floorId)
    );
    filter.setFilter('floorId', prunedFloors);

    const currentLocations: string[] = filter.getFilter('locationId') || [];
    const validLocations = (locations || [])
      .filter(l => validFloors.includes(l.floorId))
      .map(l => l.id);

    const prunedLocations = currentLocations.filter(locId =>
      validLocations.includes(locId)
    );
    filter.setFilter('locationId', prunedLocations);
  };
    

  const handleOnFloorChange = (floorIds: string[]) => {
    filter.setFilter('floorId', floorIds);


    const currentLocations: string[] = filter.getFilter('locationId') || [];
    const validLocations = (locations || [])
      .filter(l => floorIds.includes(l.floorId))
      .map(l => l.id);

    const prunedLocations = currentLocations.filter(locId =>
      validLocations.includes(locId)
    );
    filter.setFilter('locationId', prunedLocations);
  };

  const filteredFloors: Floor[] = !selectedBuildingIds.length
    ? (floors || [])
    : (floors || []).filter(f => selectedBuildingIds.includes(f.buildingId));

  const sortedFloors = filteredFloors
    .map(f => {
      const buildingName = buildingMap.get(f.buildingId) ?? '';
      return { ...f, label: `[${buildingName}] ${f.name}` };
    })
    .sort((a, b) => {
      const buildingA = buildingMap.get(a.buildingId) ?? '';
      const buildingB = buildingMap.get(b.buildingId) ?? '';
      return buildingA === buildingB
        ? a.name.localeCompare(b.name)
        : buildingA.localeCompare(buildingB);
    });
    

  const filteredLocations: Location[] = !selectedFloorIds.length
    ? (locations || [])
    : (locations || []).filter(l => selectedFloorIds.includes(l.floorId));

  const sortedLocations = filteredLocations
    .map(l => {
      const floor = floorMap.get(l.floorId);
      const buildingName = floor ? buildingMap.get(floor.buildingId) ?? '' : '';
      const floorName = floor?.name ?? '';
      return {
        ...l,
        label: `[${buildingName}] [${floorName}] ${l.name}`,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

      

  const locationLabelMap: Map<string, string> = new Map(
    sortedLocations.map(l => [l.id, l.label])
  );
      

  const floorLabelMap: Map<string, string> = new Map(
    sortedFloors.map(l => [l.id, l.label])
  );
  
  const handleOnAssetTypeChange = (assetTypeIds: string[]) => {
    filter.setFilter("assetTypeId", assetTypeIds);

    const currentSubAssets: string[] = filter.getFilter("subAssetTypeId") || [];
    const validSubAssets = (subAssetTypes || [])
      .filter((sa) => assetTypeIds.includes(sa.assetTypeId)) 
      .map((sa) => sa.id);

    const prunedSubAssets = currentSubAssets.filter((saId) =>
      validSubAssets.includes(saId)
    );
    filter.setFilter("subAssetTypeId", prunedSubAssets);
  };
    
  
  const filteredSubAssets = !selectedAssetTypeIds.length
  ? subAssetTypes || []
  : (subAssetTypes || []).filter((sa) =>
      selectedAssetTypeIds.includes(sa.assetTypeId)
    );
  

  const subAssetLabelMap: Map<string, string> = new Map(
    filteredSubAssets.map((sa) => {
      const assetName =
        assetTypes?.find((a) => a.id === sa.assetTypeId)?.name ?? "";
      return [sa.id, `[${assetName}] ${sa.name}`];
    })
  );

  const sortedSubAssets = filteredSubAssets
    .map((sa) => ({
      ...sa,
      label: subAssetLabelMap.get(sa.id) || sa.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const locationTagRender = (props: any) => {
    const { label, value, closable, onClose } = props;
    const locationLabel = locationLabelMap.get(value) || label;
    
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{ 
          margin: '2px', 
          padding: '2px 6px',
          maxWidth: '100%',
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          height: 'auto'
        }}
      >
        {locationLabel}
      </Tag>
    );
  };

  const floorTagRender = (props: any) => {
    const { label, value, closable, onClose } = props;
    const floorLabel = floorLabelMap.get(value) || label;
    
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{ 
          margin: '2px', 
          padding: '2px 6px',
          maxWidth: '100%',
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          height: 'auto'
        }}
      >
        {floorLabel}
      </Tag>
    );
  };

  const subAssetTagRender = (props: any) => {
        const { label, value, closable, onClose } = props;
        const subAssetLabel = subAssetLabelMap.get(value) || label;
        
        return (
          <Tag
            closable={closable}
            onClose={onClose}
            style={{ 
              margin: '2px', 
              padding: '2px 6px',
              maxWidth: '100%',
              whiteSpace: 'normal',
              lineHeight: '1.4em',
              height: 'auto'
            }}
          >
            {subAssetLabel}
          </Tag>
        );
      };

  return (
    <FilterContainer onResetFilters={filter.resetFilters} onSubmit={onSubmit} isSubmitting={undefined}>
      <Collapse defaultActiveKey={['general', 'location', 'assets', 'created', 'other']} ghost>
        

        <Panel header="General Info" key="general">
          <Form layout="vertical">
            <Form.Item label="Ticket Number">
              <Input allowClear value={filter.getFilter('ticketNumber')} onChange={e => filter.setFilter('ticketNumber', e.target.value)} />
            </Form.Item>
            <Form.Item label="Ticket Description">
              <Input allowClear value={filter.getFilter('ticketDescription')} onChange={e => filter.setFilter('ticketDescription', e.target.value)} />
            </Form.Item>
            <Form.Item label="Work Order">
              <Input allowClear value={filter.getFilter('workOrderReference')} onChange={e => filter.setFilter('workOrderReference', e.target.value)} />
            </Form.Item>
            <Form.Item label="Unassigned Only" valuePropName="checked">
              <Checkbox checked={filter.getFilter('unassignedOnly')} onChange={e => filter.setFilter('unassignedOnly', e.target.checked)} />
            </Form.Item>
            <Form.Item label="Ticket Source">
              <Select 
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.children ?? '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={filter.getFilter('entrySource')} 
                onChange={val => filter.setFilter('entrySource', val)}>
                <Select.Option value="Email">Email</Select.Option>
                <Select.Option value="Phone">Phone</Select.Option>
                <Select.Option value="In Person">In Person</Select.Option>
                <Select.Option value="QR Code">QR Code</Select.Option>
                <Select.Option value="Other">Other</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Panel>

        {/* Location */}
        <Panel header="Location" key="location">
          <Form layout="vertical">
            <Form.Item label="Building">
              <Select 
              mode="multiple"
              allowClear
              showSearch
              filterOption={(input, option) => {
                const label = String(option?.label ?? '');
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              value={selectedBuildingIds} 
              onChange={handleOnBuildingChange} 
              options={buildings?.map(b => ({ label: b.name, value: b.id }))} />
            </Form.Item>
            <Form.Item label="Floor">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const text = String((option as any).searchText || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                value={selectedFloorIds}
                onChange={handleOnFloorChange}
                tagRender={floorTagRender}
                dropdownRender={menu => (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {menu}
                  </div>
                )}
                optionLabelProp="label"
                options={sortedFloors.map(item => ({
                  label: (
                    <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                      {item.label}
                    </div>
                  ),
                  value: item.id,
                  searchText: item.label,
                }))}
              />
            </Form.Item>
            <Form.Item label="Location">
              <Select
                mode="multiple"
                allowClear
                value={selectedLocationIds}
                onChange={val => filter.setFilter('locationId', val)}
                tagRender={locationTagRender}
                showSearch
                filterOption={(input, option) => {
                  const text = String((option as any).searchText || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                dropdownRender={menu => (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {menu}
                  </div>
                )}
                optionLabelProp="label"
                options={sortedLocations.map(item => ({
                  label: (
                    <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                      {item.label}
                    </div>
                  ),
                  value: item.id,
                  searchText: item.label,
                }))}
              />
            </Form.Item>

            <Form.Item label="Site">
  <Select
    mode="multiple"
    allowClear
    showSearch
    filterOption={(input, option) => {
      const label = String(option?.label ?? '');
      return label.toLowerCase().includes(input.toLowerCase());
    }}
    value={selectedSiteIds}
    onChange={handleOnSiteChange}
    options={sites?.map(s => ({ label: s.name, value: s.id }))}
  />
</Form.Item>

<Form.Item label="Team">
  <Select
    mode="multiple"
    allowClear
    showSearch
    filterOption={(input, option) => {
      const text = String((option as any).searchText || '');
      return text.toLowerCase().includes(input.toLowerCase());
    }}
    value={selectedTeamIds}
    onChange={val => filter.setFilter('teamIds', val)}
    tagRender={teamTagRender}
    dropdownRender={menu => (
      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {menu}
      </div>
    )}
    optionLabelProp="label"
    options={filteredTeams.map(t => ({
      label: <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>{t.label}</div>,
      value: t.id,
      searchText: t.label,
    }))}
  />
</Form.Item>
          </Form>
        </Panel>

        {/* Asset Info */}
        <Panel header="Asset Info" key="assets">
          <Form layout="vertical">
            <Form.Item label="Asset Type">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.label ?? '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={selectedAssetTypeIds}
                onChange={handleOnAssetTypeChange}
                options={assetTypes?.map((a) => ({ label: a.name, value: a.id }))}
                />
            </Form.Item>
            <Form.Item label="Sub Asset Type">
              <Select
                 mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const text = String((option as any).searchText || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                value={selectedSubAssetTypeIds}
                onChange={(val) => filter.setFilter('subAssetTypeId', val)}
                tagRender={subAssetTagRender}
                dropdownRender={menu => (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {menu}
                  </div>
                )}
                optionLabelProp="label"
                options={sortedSubAssets.map(item => ({
                  label: (
                    <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                      {item.label}
                    </div>
                  ),
                  value: item.id,
                  searchText: item.label,
                }))}
              />
            </Form.Item>
          </Form>
        </Panel>

        {/* Created Info */}
        <Panel header="Created Info" key="created">
          <Form layout="vertical">
            <Form.Item label="Created By">
              <Select 
              mode="multiple"
              allowClear
              showSearch
              filterOption={(input, option) => {
                const text = String((option as any).label  || '');
                return text.toLowerCase().includes(input.toLowerCase());
              }}
              value={filter.getFilter('createdByUserId')} 
              onChange={val => filter.setFilter('createdByUserId', val)} 
              options={allUsers?.map(u => ({ label: u.name, value: u.id }))} />
            </Form.Item>
            <Form.Item label="Created On">
              <DateRangePicker
                onStartDateChange={val => filter.setFilter('startDate', val?.toISO())}
                onEndDateChange={val => filter.setFilter('endDate', val?.toISO())}
                startDateValue={filter.getFilter('startDate') ? DateTime.fromISO(filter.getFilter('startDate')) : undefined}
                endDateValue={filter.getFilter('endDate') ? DateTime.fromISO(filter.getFilter('endDate')) : undefined}
              />
            </Form.Item>
          </Form>
        </Panel>

        <Panel header="Other Filters" key="other">
          <Form layout="vertical">
            <Form.Item label="Contact Details">
              <Input allowClear value={filter.getFilter('contactEmail')} onChange={e => filter.setFilter('contactEmail', e.target.value)} />
            </Form.Item>
            <Form.Item label="Updates">
              <Select value={filter.getFilter('hasUpdates')} onChange={val => filter.setFilter('hasUpdates', val)}>
                <Select.Option value="">All</Select.Option>
                <Select.Option value={true}>Has Updates</Select.Option>
                <Select.Option value={false}>No Updates</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="Status">
              <Select value={filter.getFilter('status')} onChange={val => filter.setFilter('status', val)}>
                <Select.Option value="All">All</Select.Option>
                <Select.Option value="Open">Open</Select.Option>
                <Select.Option value="Closed">Closed</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Panel>

      </Collapse>
    </FilterContainer>
  );
}
